/**
 * Admin Add Collection Page
 *
 * Provides a form to create a new collection directly.
 * This replaces the old whitelisted issuer flow.
 * Creating a collection automatically triggers indexing.
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCollection, uploadCollectionImage } from '../services/adminApi';
import { MARKETPLACE_OPTIONS } from '../lib/marketplace-constants';
import type { PreferredMarketplace } from '../types/admin';

interface FormData {
  name: string;
  creator_address: string;
  taxon: string;
  slug: string;
  description: string;
  image_url: string;
  banner_url: string;
  external_url: string;
  symbol: string;
  royalty_percentage: string;
  royalty_address: string;
  mint_url: string;
  is_verified: boolean;
  is_top_community: boolean;
  preferred_marketplace: PreferredMarketplace;
  priority: string;
  xrpl_voteboard_id: string;
}

const initialFormData: FormData = {
  name: '',
  creator_address: '',
  taxon: '',
  slug: '',
  description: '',
  image_url: '',
  banner_url: '',
  external_url: '',
  symbol: '',
  royalty_percentage: '0',
  royalty_address: '',
  mint_url: '',
  is_verified: false,
  is_top_community: false,
  preferred_marketplace: 'xrp.cafe',
  priority: '5',
  xrpl_voteboard_id: '',
};

export function AdminAddCollectionPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isIssuerWide, setIsIssuerWide] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Image upload state
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [bannerImageFile, setBannerImageFile] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
  const [bannerImagePreview, setBannerImagePreview] = useState<string | null>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const profileInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  // Validate XRPL address format (r-address)
  const isValidXrplAddress = (address: string): boolean => {
    // XRPL addresses start with 'r', are 25-35 characters, and use base58 alphabet
    const xrplAddressRegex = /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/;
    return xrplAddressRegex.test(address);
  };

  // Validate URL format
  const isValidUrl = (url: string): boolean => {
    if (!url) return true; // Optional fields
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Handle profile image file selection
  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Invalid image format. Please use JPEG, PNG, GIF, or WebP.');
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Image file is too large. Maximum size is 10MB.');
        return;
      }
      setProfileImageFile(file);
      setProfileImagePreview(URL.createObjectURL(file));
      // Clear the URL field since we're using file upload
      setFormData(prev => ({ ...prev, image_url: '' }));
    }
  };

  // Handle banner image file selection
  const handleBannerImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Invalid image format. Please use JPEG, PNG, GIF, or WebP.');
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Image file is too large. Maximum size is 10MB.');
        return;
      }
      setBannerImageFile(file);
      setBannerImagePreview(URL.createObjectURL(file));
      // Clear the URL field since we're using file upload
      setFormData(prev => ({ ...prev, banner_url: '' }));
    }
  };

  // Clear profile image
  const clearProfileImage = () => {
    setProfileImageFile(null);
    setProfileImagePreview(null);
    if (profileInputRef.current) {
      profileInputRef.current.value = '';
    }
  };

  // Clear banner image
  const clearBannerImage = () => {
    setBannerImageFile(null);
    setBannerImagePreview(null);
    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // Validate name
      if (!formData.name.trim()) {
        throw new Error('Name is required');
      }
      if (formData.name.trim().length > 255) {
        throw new Error('Name must be 255 characters or less');
      }

      // Validate creator address
      const creatorAddress = formData.creator_address.trim();
      if (!creatorAddress) {
        throw new Error('Creator address is required');
      }
      if (!isValidXrplAddress(creatorAddress)) {
        throw new Error('Invalid XRPL address format. Address must start with "r" and be 25-35 characters.');
      }

      // Validate slug
      const slug = formData.slug.trim();
      if (!slug) {
        throw new Error('Slug is required');
      }
      if (slug.length < 3 || slug.length > 24) {
        throw new Error('Slug must be between 3 and 24 characters');
      }
      if (!/^[a-zA-Z0-9_-]+$/.test(slug)) {
        throw new Error('Slug can only contain letters, numbers, dashes, and underscores');
      }

      // Parse and validate taxon (or null for issuer-wide)
      let taxonValue: number | null = null;
      if (!isIssuerWide) {
        if (!formData.taxon.trim()) {
          throw new Error('Taxon is required (or select "Issuer-Wide Collection")');
        }
        const taxonNumber = parseInt(formData.taxon, 10);
        if (isNaN(taxonNumber) || taxonNumber < 0 || taxonNumber > 4294967295) {
          throw new Error('Taxon must be a valid integer between 0 and 4,294,967,295');
        }
        taxonValue = taxonNumber;
      }

      // Validate optional URL fields
      if (formData.image_url.trim() && !isValidUrl(formData.image_url.trim())) {
        throw new Error('Image URL is not a valid URL');
      }
      if (formData.banner_url.trim() && !isValidUrl(formData.banner_url.trim())) {
        throw new Error('Banner URL is not a valid URL');
      }
      if (formData.external_url.trim() && !isValidUrl(formData.external_url.trim())) {
        throw new Error('External URL is not a valid URL');
      }
      if (formData.mint_url.trim() && !isValidUrl(formData.mint_url.trim())) {
        throw new Error('Mint URL is not a valid URL');
      }

      // Validate royalty address if provided
      if (formData.royalty_address.trim() && !isValidXrplAddress(formData.royalty_address.trim())) {
        throw new Error('Invalid royalty address format. Address must start with "r" and be 25-35 characters.');
      }

      // Parse and validate numeric fields
      const royaltyPercentage = parseFloat(formData.royalty_percentage) || 0;
      if (royaltyPercentage < 0 || royaltyPercentage > 100) {
        throw new Error('Royalty percentage must be between 0 and 100');
      }

      const priority = parseInt(formData.priority, 10) || 5;
      if (priority < 0 || priority > 10) {
        throw new Error('Priority must be between 0 and 10');
      }

      // Parse optional voteboard ID
      const voteboardId = formData.xrpl_voteboard_id.trim()
        ? parseInt(formData.xrpl_voteboard_id.trim(), 10)
        : null;
      if (voteboardId !== null && (isNaN(voteboardId) || voteboardId < 1)) {
        throw new Error('VoteBoard ID must be a positive integer');
      }

      // Submit to API (create collection first)
      const result = await createCollection({
        name: formData.name.trim(),
        creator_address: formData.creator_address.trim(),
        taxon: taxonValue,
        slug: slug,
        description: formData.description.trim() || undefined,
        image_url: formData.image_url.trim() || undefined,
        banner_url: formData.banner_url.trim() || undefined,
        external_url: formData.external_url.trim() || undefined,
        symbol: formData.symbol.trim() || undefined,
        royalty_percentage: royaltyPercentage || undefined,
        royalty_address: formData.royalty_address.trim() || undefined,
        mint_url: formData.mint_url.trim() || undefined,
        is_verified: formData.is_verified,
        is_top_community: formData.is_top_community,
        preferred_marketplace: formData.preferred_marketplace,
        priority: priority,
        xrpl_voteboard_id: voteboardId,
      });

      // Upload images if files were selected
      const collectionId = result.collection?.id;
      if (collectionId && (profileImageFile || bannerImageFile)) {
        setUploadingImages(true);
        try {
          if (profileImageFile) {
            await uploadCollectionImage(collectionId, profileImageFile, 'profile');
          }
          if (bannerImageFile) {
            await uploadCollectionImage(collectionId, bannerImageFile, 'banner');
          }
        } catch (uploadError: any) {
          // Collection was created but image upload failed
          // Log the error but don't fail the whole operation
          console.error('Image upload failed:', uploadError);
        } finally {
          setUploadingImages(false);
        }
      }

      setSuccess(true);

      // Redirect to collections page after 2 seconds
      setTimeout(() => {
        navigate('/admin/collections');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to create collection');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value as PreferredMarketplace,
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked,
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <a
              href="/admin/collections"
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              &larr; Back to Collections
            </a>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Create New Collection</h1>
          <p className="mt-2 text-sm text-gray-600">
            Create a new collection. Indexing will start automatically after creation.
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 rounded-lg bg-green-50 border border-green-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Collection created successfully! Indexing started. Redirecting...
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Required Fields Section */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Required Information</h2>

              {/* Collection Name */}
              <div className="mb-4">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Collection Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  maxLength={255}
                  placeholder="My Awesome Collection"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={submitting || success}
                />
              </div>

              {/* Creator Address */}
              <div className="mb-4">
                <label htmlFor="creator_address" className="block text-sm font-medium text-gray-700 mb-2">
                  Creator/Issuer Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="creator_address"
                  name="creator_address"
                  value={formData.creator_address}
                  onChange={handleChange}
                  required
                  minLength={25}
                  maxLength={64}
                  placeholder="rAbc123..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={submitting || success}
                />
                <p className="mt-1 text-sm text-gray-500">
                  XRPL address of the NFT collection issuer
                </p>
              </div>

              {/* Issuer-Wide Toggle */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isIssuerWide}
                    onChange={(e) => {
                      setIsIssuerWide(e.target.checked);
                      if (e.target.checked) {
                        setFormData({ ...formData, taxon: '' });
                      }
                    }}
                    disabled={submitting || success}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-900">
                    Issuer-Wide Collection (All Taxons)
                  </span>
                </label>
                <p className="mt-2 ml-7 text-sm text-gray-600">
                  Include ALL NFTs from this issuer in one collection, regardless of their taxon values.
                </p>
              </div>

              {/* Taxon */}
              {!isIssuerWide && (
                <div className="mb-4">
                  <label htmlFor="taxon" className="block text-sm font-medium text-gray-700 mb-2">
                    Taxon <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="taxon"
                    name="taxon"
                    value={formData.taxon}
                    onChange={handleChange}
                    required={!isIssuerWide}
                    min={0}
                    max={4294967295}
                    placeholder="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={submitting || success}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Collection taxon number (0 to 4,294,967,295)
                  </p>
                </div>
              )}

              {/* Slug */}
              <div>
                <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-2">
                  URL Slug <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  required
                  minLength={3}
                  maxLength={24}
                  pattern="[a-zA-Z0-9_-]+"
                  placeholder="my-awesome-collection"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={submitting || success}
                />
                <p className="mt-1 text-sm text-gray-500">
                  URL-friendly identifier (3-24 characters, letters, numbers, dashes, underscores)
                </p>
              </div>
            </div>

            {/* Optional Fields Section */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Collection Details (Optional)</h2>

              {/* Description */}
              <div className="mb-4">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  maxLength={2000}
                  rows={3}
                  placeholder="Describe your collection..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={submitting || success}
                />
              </div>

              {/* Profile Image Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Image
                </label>
                <div className="space-y-3">
                  {/* File Upload */}
                  <div className="flex items-center gap-3">
                    <input
                      ref={profileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleProfileImageChange}
                      className="hidden"
                      disabled={submitting || success}
                    />
                    <button
                      type="button"
                      onClick={() => profileInputRef.current?.click()}
                      disabled={submitting || success}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Choose File
                    </button>
                    {profileImageFile && (
                      <span className="text-sm text-gray-600">{profileImageFile.name}</span>
                    )}
                    {profileImageFile && (
                      <button
                        type="button"
                        onClick={clearProfileImage}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {/* Preview */}
                  {profileImagePreview && (
                    <div className="relative w-24 h-24">
                      <img
                        src={profileImagePreview}
                        alt="Profile preview"
                        className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                      />
                    </div>
                  )}
                  {/* Or URL fallback */}
                  {!profileImageFile && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Or enter a URL:</p>
                      <input
                        type="url"
                        id="image_url"
                        name="image_url"
                        value={formData.image_url}
                        onChange={handleChange}
                        placeholder="https://example.com/image.png"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        disabled={submitting || success}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Banner Image Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banner Image
                </label>
                <div className="space-y-3">
                  {/* File Upload */}
                  <div className="flex items-center gap-3">
                    <input
                      ref={bannerInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleBannerImageChange}
                      className="hidden"
                      disabled={submitting || success}
                    />
                    <button
                      type="button"
                      onClick={() => bannerInputRef.current?.click()}
                      disabled={submitting || success}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Choose File
                    </button>
                    {bannerImageFile && (
                      <span className="text-sm text-gray-600">{bannerImageFile.name}</span>
                    )}
                    {bannerImageFile && (
                      <button
                        type="button"
                        onClick={clearBannerImage}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {/* Preview */}
                  {bannerImagePreview && (
                    <div className="relative">
                      <img
                        src={bannerImagePreview}
                        alt="Banner preview"
                        className="w-full max-w-md h-32 object-cover rounded-lg border border-gray-200"
                      />
                    </div>
                  )}
                  {/* Or URL fallback */}
                  {!bannerImageFile && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Or enter a URL:</p>
                      <input
                        type="url"
                        id="banner_url"
                        name="banner_url"
                        value={formData.banner_url}
                        onChange={handleChange}
                        placeholder="https://example.com/banner.png"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        disabled={submitting || success}
                      />
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Recommended: 1200x400 pixels for optimal display
                </p>
              </div>

              {/* Website URL */}
              <div className="mb-4">
                <label htmlFor="external_url" className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  id="external_url"
                  name="external_url"
                  value={formData.external_url}
                  onChange={handleChange}
                  placeholder="https://mycollection.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={submitting || success}
                />
              </div>

              {/* Preferred Marketplace */}
              <div>
                <label htmlFor="preferred_marketplace" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Marketplace
                </label>
                <select
                  id="preferred_marketplace"
                  name="preferred_marketplace"
                  value={formData.preferred_marketplace}
                  onChange={handleSelectChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  disabled={submitting || success}
                >
                  {MARKETPLACE_OPTIONS.filter(opt => opt.value !== 'p2p').map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  The default marketplace for listing NFTs from this collection
                </p>
              </div>
            </div>

            {/* Advanced Options */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <svg
                  className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                Advanced Options
              </button>

              {showAdvanced && (
                <div className="mt-4 space-y-4 pl-4 border-l-2 border-gray-200">
                  {/* Symbol */}
                  <div>
                    <label htmlFor="symbol" className="block text-sm font-medium text-gray-700 mb-2">
                      Symbol
                    </label>
                    <input
                      type="text"
                      id="symbol"
                      name="symbol"
                      value={formData.symbol}
                      onChange={handleChange}
                      maxLength={10}
                      placeholder="MYCOL"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={submitting || success}
                    />
                  </div>

                  {/* Royalty Percentage */}
                  <div>
                    <label htmlFor="royalty_percentage" className="block text-sm font-medium text-gray-700 mb-2">
                      Royalty Percentage
                    </label>
                    <input
                      type="number"
                      id="royalty_percentage"
                      name="royalty_percentage"
                      value={formData.royalty_percentage}
                      onChange={handleChange}
                      min={0}
                      max={100}
                      step={0.1}
                      placeholder="5"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={submitting || success}
                    />
                  </div>

                  {/* Royalty Address */}
                  <div>
                    <label htmlFor="royalty_address" className="block text-sm font-medium text-gray-700 mb-2">
                      Royalty Address
                    </label>
                    <input
                      type="text"
                      id="royalty_address"
                      name="royalty_address"
                      value={formData.royalty_address}
                      onChange={handleChange}
                      placeholder="rAbc123..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={submitting || success}
                    />
                  </div>

                  {/* Mint URL */}
                  <div>
                    <label htmlFor="mint_url" className="block text-sm font-medium text-gray-700 mb-2">
                      Mint URL
                    </label>
                    <input
                      type="url"
                      id="mint_url"
                      name="mint_url"
                      value={formData.mint_url}
                      onChange={handleChange}
                      placeholder="https://mint.mycollection.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={submitting || success}
                    />
                  </div>

                  {/* Indexing Priority */}
                  <div>
                    <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
                      Indexing Priority (0-10)
                    </label>
                    <input
                      type="number"
                      id="priority"
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      min={0}
                      max={10}
                      placeholder="5"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={submitting || success}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Higher priority collections are indexed first
                    </p>
                  </div>

                  {/* XRPL VoteBoard ID */}
                  <div>
                    <label htmlFor="xrpl_voteboard_id" className="block text-sm font-medium text-gray-700 mb-2">
                      XRPL VoteBoard ID
                    </label>
                    <input
                      type="number"
                      id="xrpl_voteboard_id"
                      name="xrpl_voteboard_id"
                      value={formData.xrpl_voteboard_id}
                      onChange={handleChange}
                      min={1}
                      placeholder="e.g., 42"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={submitting || success}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      ID from <a href="https://nft.xrplvoteboard.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">XRPL VoteBoard</a> for syncing community scores
                    </p>
                  </div>

                  {/* Checkboxes */}
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_verified"
                        checked={formData.is_verified}
                        onChange={handleCheckboxChange}
                        disabled={submitting || success}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Verified Collection</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_top_community"
                        checked={formData.is_top_community}
                        onChange={handleCheckboxChange}
                        disabled={submitting || success}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Top Community Collection</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">What happens next?</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>* The collection will be created immediately</li>
                <li>* An indexing job will be queued automatically</li>
                <li>* NFTs from this collection will start appearing as they are indexed</li>
              </ul>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={submitting || success || uploadingImages}
                className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors ${
                  submitting || success || uploadingImages
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {submitting || uploadingImages ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {uploadingImages ? 'Uploading images...' : 'Creating...'}
                  </span>
                ) : (
                  'Create Collection'
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/collections')}
                disabled={submitting}
                className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
